import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700 border-slate-300",
  pending_approval: "bg-amber-50 text-amber-900 border-amber-200",
  published: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

const EMPTY = { title: "", summary: "", body: "", image: "", status: "draft", featured: false };

export default function AdminNews() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => adminApi.get("/admin/news").then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        title: editing.title, summary: editing.summary || null, body: editing.body || null,
        image: editing.image || null, status: editing.status, featured: editing.featured,
      };
      if (editing.id) {
        await adminApi.patch(`/admin/news/${editing.id}`, payload);
        toast.success("Story updated");
      } else {
        await adminApi.post("/admin/news", payload);
        toast.success("Story created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (post) => {
    try {
      await adminApi.delete(`/admin/news/${post.id}`);
      toast.success("Story deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <h1 className="font-heading font-bold text-2xl text-foreground" data-testid="admin-news-title">Newsroom</h1>
        <Button onClick={() => setEditing({ ...EMPTY })} data-testid="news-create-button"><Plus className="h-4 w-4 mr-2" /> New story</Button>
      </div>
      <p className="text-xs text-muted-foreground mb-6">
        Published stories appear on /news and the homepage. Featured stories are highlighted first.
      </p>

      <Card><CardContent className="p-0">
        <Table data-testid="admin-news-table">
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Featured</TableHead>
              <TableHead>Published</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {posts.map((p) => (
              <TableRow key={p.id} className="cursor-pointer" onClick={() => setEditing({ ...EMPTY, ...p, summary: p.summary || "", body: p.body || "", image: p.image || "" })} data-testid={`news-row-${p.slug}`}>
                <TableCell className="font-medium max-w-sm"><span className="line-clamp-1">{p.title}</span><p className="text-xs text-muted-foreground font-mono">{p.slug}</p></TableCell>
                <TableCell><Badge variant="outline" className={STATUS_STYLE[p.status]}>{p.status.replace("_", " ")}</Badge></TableCell>
                <TableCell>{p.featured ? <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300">featured</Badge> : "—"}</TableCell>
                <TableCell className="font-mono text-xs">{p.published_at?.slice(0, 10) || "—"}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(p)} aria-label="Delete story"><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          {editing && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{editing.id ? "Edit story" : "New story"}</DialogTitle></DialogHeader>
              <div>
                <Label>Title</Label>
                <Input className="mt-1.5" value={editing.title} onChange={(e) => setEditing((x) => ({ ...x, title: e.target.value }))} data-testid="news-form-title" />
              </div>
              <div>
                <Label>Summary</Label>
                <Textarea className="mt-1.5" rows={3} value={editing.summary} onChange={(e) => setEditing((x) => ({ ...x, summary: e.target.value }))} data-testid="news-form-summary" />
              </div>
              <div>
                <Label>Body</Label>
                <Textarea className="mt-1.5" rows={8} value={editing.body} onChange={(e) => setEditing((x) => ({ ...x, body: e.target.value }))} data-testid="news-form-body" />
              </div>
              <div>
                <Label>Image URL (optional)</Label>
                <Input className="mt-1.5" value={editing.image} onChange={(e) => setEditing((x) => ({ ...x, image: e.target.value }))} data-testid="news-form-image" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4 items-end">
                <div>
                  <Label>Status</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing((x) => ({ ...x, status: v }))}>
                    <SelectTrigger className="mt-1.5" data-testid="news-form-status"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="pending_approval">Pending approval</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                  <span className="text-sm font-medium">Featured on homepage</span>
                  <Switch checked={editing.featured} onCheckedChange={(v) => setEditing((x) => ({ ...x, featured: v }))} data-testid="news-form-featured" />
                </div>
              </div>
              <Button onClick={save} disabled={busy || editing.title.length < 2} data-testid="news-form-save">
                {busy ? "Saving…" : editing.id ? "Save changes" : "Create story"}
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
